import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { BRAND, IMAGES } from "../data/content";
import { useT, useLang } from "../i18n/LanguageContext";

const NAV = [
  { to: "/", key: "nav.home" },
  { to: "/services", key: "nav.services" },
  { to: "/cruise-tours", key: "nav.journeys" },
];

const GUIDES = {
  transfers: [
    { to: "/livorno-cruise-transfers", key: "footer.cruiseGuide" },
    { to: "/florence-pisa-shore-excursion-from-livorno", key: "footer.florencePisaGuide" },
    { to: "/la-spezia-shore-excursion", key: "footer.laSpeziaGuide" },
    { to: "/pisa-florence-airport-transfers", key: "footer.airportGuide" },
    { to: "/viareggio-carrara-yacht-transfers", key: "footer.yachtGuide" },
  ],
  tours: [
    { to: "/private-driver-tuscany", key: "footer.privateDriverGuide" },
    { to: "/tuscany-wine-tours", key: "footer.wineGuide" },
    { to: "/florence-private-tour", key: "footer.florenceGuide" },
    { to: "/pisa-leaning-tower-tour", key: "footer.pisaGuide" },
    { to: "/cinque-terre-private-tour", key: "footer.cinqueTerreGuide" },
    { to: "/val-orcia-private-tour", key: "footer.valdorciaGuide" },
  ],
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const { pathname } = useLocation();
  const t = useT();
  const { lang, setLang } = useLang();
  const toggleLang = () => setLang(lang === "it" ? "en" : "it");
  const targetFlag = lang === "it"
    ? { src: "https://flagcdn.com/gb.svg", alt: "Switch to English" }
    : { src: "https://flagcdn.com/it.svg", alt: "Passa all'italiano" };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 w-full z-50 px-5 md:px-10 py-4 flex items-center justify-between transition-colors duration-500 ${
          scrolled || open ? "backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5" : "bg-transparent"
        }`}
      >
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src={IMAGES.logoMark} alt="Your Black Limo" className="h-9 w-9 object-contain" />
          <span className="font-wordmark text-base md:text-lg tracking-[0.2em] text-[#F5F5F0] hidden sm:block">
            YOUR BLACK LIMO
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to === "/" ? "home" : n.to.replace("/", "")}`}
              className={`text-sm tracking-wide transition-colors hover:text-[#C5A059] ${
                pathname === n.to ? "text-[#C5A059]" : "text-white/70"
              }`}
            >
              {t(n.key)}
            </Link>
          ))}
          <div
            className="relative"
            data-testid="nav-guides-wrapper"
            onMouseEnter={() => setGuidesOpen(true)}
            onMouseLeave={() => setGuidesOpen(false)}
          >
            <button
              data-testid="nav-guides"
              onClick={() => setGuidesOpen((v) => !v)}
              aria-expanded={guidesOpen}
              className={`flex items-center gap-1 text-sm tracking-wide transition-colors hover:text-[#C5A059] ${guidesOpen ? "text-[#C5A059]" : "text-white/70"}`}
            >
              {t("nav.guides")}
              <ChevronDown size={14} className={`transition-transform duration-300 ${guidesOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {guidesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  data-testid="guides-dropdown"
                  className="absolute top-full right-0 pt-5"
                >
                  <div className="w-[540px] grid grid-cols-2 gap-x-10 gap-y-1 bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 p-8 shadow-2xl">
                    <div>
                      <p className="overline mb-4 text-[#C5A059]">{t("nav.guidesTransfers")}</p>
                      {GUIDES.transfers.map((g) => (
                        <Link
                          key={g.to}
                          to={g.to}
                          onClick={() => setGuidesOpen(false)}
                          data-testid={`guides-dropdown${g.to}`}
                          className="block py-2 text-sm text-white/70 hover:text-[#C5A059] transition-colors"
                        >
                          {t(g.key)}
                        </Link>
                      ))}
                    </div>
                    <div>
                      <p className="overline mb-4 text-[#C5A059]">{t("nav.guidesTours")}</p>
                      {GUIDES.tours.map((g) => (
                        <Link
                          key={g.to}
                          to={g.to}
                          onClick={() => setGuidesOpen(false)}
                          data-testid={`guides-dropdown${g.to}`}
                          className="block py-2 text-sm text-white/70 hover:text-[#C5A059] transition-colors"
                        >
                          {t(g.key)}
                        </Link>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            aria-label={targetFlag.alt}
            title={targetFlag.alt}
            className="flex items-center border border-white/20 hover:border-[#C5A059] p-1.5 transition-colors"
          >
            <img src={targetFlag.src} alt="" className="w-6 h-4 object-cover rounded-[2px]" />
          </button>
          <a
            href="#contact"
            data-testid="header-cta"
            className="text-xs uppercase tracking-[0.2em] border border-[#C5A059] text-[#C5A059] px-6 py-3 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors"
          >
            {t("nav.reserve")}
          </a>
        </nav>

        <div className="flex items-center gap-4 md:hidden">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle-mobile"
            aria-label={targetFlag.alt}
            title={targetFlag.alt}
            className="flex items-center border border-white/20 p-1.5"
          >
            <img src={targetFlag.src} alt="" className="w-6 h-4 object-cover rounded-[2px]" />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="text-[#F5F5F0]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-[64px] left-0 w-full z-40 md:hidden backdrop-blur-xl bg-[#0A0A0A]/95 border-b border-white/5"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="font-serif text-2xl text-[#F5F5F0]">
                  {t(n.key)}
                </Link>
              ))}
              <div className="border-t border-white/10 pt-5 mt-1">
                <p className="overline mb-4 text-[#C5A059]">{t("nav.guides")}</p>
                <div className="flex flex-col gap-3">
                  {[...GUIDES.transfers, ...GUIDES.tours].map((g) => (
                    <Link key={g.to} to={g.to} data-testid={`mobile-guide${g.to}`} className="text-white/80 text-base">
                      {t(g.key)}
                    </Link>
                  ))}
                </div>
              </div>
              <a href="#contact" onClick={() => setOpen(false)} className="text-xs uppercase tracking-[0.2em] border border-[#C5A059] text-[#C5A059] px-6 py-3 text-center mt-2">
                {t("nav.reserve")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
